import React from 'react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { scrollToSection } from '../../utils/scroll';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/guenolekikabou',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      ),
    },
    {
      name: 'X (Twitter)',
      url: 'https://x.com/FramerieHQ',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/guenole/',
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ];

  return (
    <footer className="bg-md-background text-white border-t border-slate-700/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 footer-grid">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center p-1">
                <img
                  src="/assets/icons/apple-icon-180x180.png"
                  alt="Logo Framerie"
                  className="w-6 h-6 relative z-10"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Framerie</h3>
                <p className="text-sm text-slate-300">
                  {t('footer.brand.tagline')}
                </p>
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('footer.brand.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">
              {t('footer.quickLinks.title')}
            </h4>
            <div className="space-y-2">
              <a
                href="https://dreambel.tech/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-slate-300 hover:text-md-secondary transition-colors duration-200 text-sm"
              >
                {t('footer.quickLinks.dreambelTech')}
              </a>
              <button
                onClick={() => scrollToSection('features')}
                className="block text-slate-300 hover:text-md-secondary transition-colors duration-200 text-sm text-left"
              >
                {t('footer.quickLinks.features')}
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="block text-slate-300 hover:text-md-secondary transition-colors duration-200 text-sm text-left"
              >
                {t('footer.quickLinks.pricing')}
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className="block text-slate-300 hover:text-md-secondary transition-colors duration-200 text-sm text-left"
              >
                {t('footer.quickLinks.faq')}
              </button>
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">
              {t('footer.social.title')}
            </h4>
            <div className="flex space-x-4 footer-social-group">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-md-surface-variant/20 rounded-lg flex items-center justify-center text-slate-300 hover:text-md-secondary hover:bg-md-surface-variant/40 transition-all duration-200 shadow-sm hover:shadow-md border border-md-outline-variant/30 social-link"
                  aria-label={`${t('footer.social.followOn')} ${link.name}`}
                >
                  {link.icon}
                </a>
              ))}
            </div>

            {/* Contact Email */}
            <div className="mt-4">
              <h5 className="text-sm font-medium text-white mb-2">
                {t('footer.contact.title')}
              </h5>
              <a
                href="mailto:framerie@dreambel.tech"
                className="text-slate-300 hover:text-md-secondary transition-colors duration-200 text-sm flex items-center space-x-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span>framerie@dreambel.tech</span>
              </a>
            </div>

            <div className="mt-6 p-4 bg-md-surface-variant/20 rounded-lg border border-md-outline-variant/30 w-[250px]">
              <p className="text-xs text-slate-400 leading-relaxed">
                <span className="block font-medium text-slate-300 mb-1">
                  {t('footer.credit.developedBy')} {t('footer.credit.company')}
                </span>
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {t('footer.credit.poweredBy')} dreambel-ui
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-slate-600/60 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm text-slate-400">
            © {currentYear} Framerie. {t('footer.legal.copyright')}
          </div>
          <div className="flex items-center space-x-6 text-sm text-slate-400">
            <button
              onClick={() => scrollToSection('faq')}
              className="hover:text-md-secondary transition-colors duration-200"
            >
              {t('footer.legal.support')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
